import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, Building2, CreditCard, Copy, CheckCircle2, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function BankPayment() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState({});
  const [formData, setFormData] = useState({
    payment_reference: '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: 30.00
  });

  useEffect(() => {
    loadBankDetails();
  }, []);

  const loadBankDetails = async () => {
    try {
      const res = await apiClient.get('/subscriptions/bank-details');
      setBankDetails(res.data);
    } catch (error) {
      toast.error('Failed to load bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value);
    setCopied({ ...copied, [field]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.payment_reference.trim()) {
      toast.error('Please enter your payment reference/confirmation number');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await apiClient.post('/subscriptions/submit-payment', {
        payment_reference: formData.payment_reference,
        payment_date: formData.payment_date,
        amount: parseFloat(formData.amount),
        payment_type: 'subscription'
      });
      
      if (res.data.status === 'approved') {
        // Update user context
        const updatedUser = { ...user, subscription_status: 'active' };
        updateUser(updatedUser);
        toast.success('Subscription activated!');
        navigate('/subscription/success');
      } else {
        toast.success('Payment submitted for verification');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Complete Your Subscription</h1>
          <p className="text-slate-600 mt-2">Pay via bank transfer to activate your account</p>
        </div>

        {/* Subscription Info */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">Monthly Subscription</h2>
              <p className="text-3xl font-black text-orange-500">${bankDetails?.subscription_amount?.toFixed(2)}<span className="text-lg text-slate-500">/month</span></p>
            </div>
          </div>
        </div>

        {/* Bank Details Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-8">
          <div className="bg-slate-900 text-white p-6">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6" />
              <h2 className="text-xl font-bold">Bank Transfer Details</h2>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Account Name */}
            <div>
              <Label className="text-slate-500 text-sm">Account Name</Label>
              <div className="flex items-center justify-between mt-1 p-3 bg-slate-50 rounded-lg">
                <span className="font-semibold text-slate-900">{bankDetails?.account_name}</span>
              </div>
            </div>

            {/* Account Number */}
            <div>
              <Label className="text-slate-500 text-sm">Account Number</Label>
              <div className="flex items-center justify-between mt-1 p-3 bg-slate-50 rounded-lg">
                <span className="font-mono text-lg font-bold text-slate-900">{bankDetails?.account_number}</span>
                <button
                  onClick={() => handleCopy('account', bankDetails?.account_number)}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  data-testid="copy-account"
                >
                  {copied.account ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5 text-slate-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Routing Number */}
            <div>
              <Label className="text-slate-500 text-sm">Routing Number</Label>
              <div className="flex items-center justify-between mt-1 p-3 bg-slate-50 rounded-lg">
                <span className="font-mono text-lg font-bold text-slate-900">{bankDetails?.routing_number}</span>
                <button
                  onClick={() => handleCopy('routing', bankDetails?.routing_number)}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  data-testid="copy-routing"
                >
                  {copied.routing ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5 text-slate-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Amount */}
            <div>
              <Label className="text-slate-500 text-sm">Amount to Pay</Label>
              <div className="flex items-center justify-between mt-1 p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                <span className="font-bold text-2xl text-orange-600">${bankDetails?.subscription_amount?.toFixed(2)}</span>
                <button
                  onClick={() => handleCopy('amount', bankDetails?.subscription_amount?.toFixed(2))}
                  className="p-2 hover:bg-orange-100 rounded-lg transition-colors"
                  data-testid="copy-amount"
                >
                  {copied.amount ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5 text-orange-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Important Instructions:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Include your email ({user?.email}) in the payment memo</li>
                    <li>Save your confirmation/reference number</li>
                    <li>Enter the reference number below after completing transfer</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Confirmation Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Confirm Your Payment</h3>
          <p className="text-slate-600 text-sm mb-6">
            After completing your bank transfer, enter the details below to activate your subscription.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reference">Payment Reference / Confirmation Number *</Label>
              <Input
                id="reference"
                value={formData.payment_reference}
                onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                placeholder="Enter your bank confirmation number"
                className="mt-1"
                required
                data-testid="payment-reference-input"
              />
            </div>
            
            <div>
              <Label htmlFor="date">Payment Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                className="mt-1"
                data-testid="payment-date-input"
              />
            </div>
            
            <div>
              <Label htmlFor="amount">Amount Paid ($)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="mt-1"
                data-testid="payment-amount-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="btn-accent w-full mt-6" 
              disabled={submitting}
              data-testid="submit-payment-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Confirm Payment <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-700">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
